import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useHostNarration } from '../narration/HostNarration.tsx';
import { useSessionContext } from '../session/SessionContext.ts';
import { effectCuesForEvent, musicTrackFor, shouldPlayActivityTransition, type EffectCue, type MusicTrack } from './audioPolicy.ts';
import './game-audio.css';

const MUSIC: Record<MusicTrack, string> = {
  map: '/audio/map-loop.mp3',
  decision: '/audio/decision-loop.mp3',
  potato: '/audio/potato-loop.mp3',
};
const EFFECTS = {
  crystal: '/audio/crystal.mp3',
  transition: '/audio/transition.mp3',
  jump: '/audio/jump.mp3',
} as const;

type EffectSound = EffectCue['sound'] | 'transition';

const MUSIC_PREFERENCE = 'momentum.audio.music';
const EFFECTS_PREFERENCE = 'momentum.audio.effects';

function savedPreference(key: string) {
  try { return window.localStorage.getItem(key) !== 'off'; } catch { return true; }
}

function savePreference(key: string, enabled: boolean) {
  try { window.localStorage.setItem(key, enabled ? 'on' : 'off'); } catch { /* Storage can be unavailable in private contexts. */ }
}

/** Persistent, user-unlocked audio for the live session. It sits above every renderer swap. */
export function GameAudioProvider({ children }: { children: ReactNode }) {
  const { view, client } = useSessionContext();
  const narration = useHostNarration();
  const active = view.child ?? view.root;
  const track = musicTrackFor(active?.moduleId);
  const paused = view.session.pause !== null;
  const [musicEnabled, setMusicEnabled] = useState(() => savedPreference(MUSIC_PREFERENCE));
  const [effectsEnabled, setEffectsEnabled] = useState(() => savedPreference(EFFECTS_PREFERENCE));
  const [effectsLoading, setEffectsLoading] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [audioError, setAudioError] = useState('');
  const musicContext = useRef<AudioContext | null>(null);
  const musicGain = useRef<GainNode | null>(null);
  const musicSource = useRef<AudioBufferSourceNode | null>(null);
  const playingTrack = useRef<MusicTrack | null>(null);
  const musicBuffers = useRef(new Map<MusicTrack, AudioBuffer>());
  const musicLoads = useRef(new Map<MusicTrack, Promise<AudioBuffer>>());
  const musicGeneration = useRef(0);
  const timers = useRef(new Set<number>());
  const effectContext = useRef<AudioContext | null>(null);
  const effectBuffers = useRef(new Map<EffectSound, AudioBuffer>());
  const effectLoads = useRef(new Map<EffectSound, Promise<AudioBuffer>>());
  const activeEffects = useRef(new Set<AudioBufferSourceNode>());
  const effectGeneration = useRef(0);
  const effectsRef = useRef(effectsEnabled);
  const musicEnabledRef = useRef(musicEnabled);
  const pausedRef = useRef(paused);
  const narrationPlayingRef = useRef(narration.playing);
  const instanceRef = useRef(active?.instanceId ?? null);
  effectsRef.current = effectsEnabled;
  musicEnabledRef.current = musicEnabled;
  pausedRef.current = paused;
  narrationPlayingRef.current = narration.playing;
  instanceRef.current = active?.instanceId ?? null;

  function audioContextClass() {
    return window.AudioContext ?? (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  }

  function ensureMusicContext() {
    const AudioContextClass = audioContextClass();
    if (!AudioContextClass) throw new Error('This browser does not support game music.');
    const context = musicContext.current ?? new AudioContextClass();
    musicContext.current = context;
    if (!musicGain.current) {
      const gain = context.createGain();
      gain.connect(context.destination);
      musicGain.current = gain;
    }
    musicGain.current.gain.value = narrationPlayingRef.current ? 0.055 : 0.16;
    return context;
  }

  function ensureEffectContext() {
    const AudioContextClass = audioContextClass();
    if (!AudioContextClass) throw new Error('This browser does not support game effects.');
    const context = effectContext.current ?? new AudioContextClass();
    effectContext.current = context;
    return context;
  }

  async function unlockAudio(showError: boolean) {
    try {
      const contexts: AudioContext[] = [];
      if (musicEnabledRef.current) contexts.push(ensureMusicContext());
      if (effectsRef.current) contexts.push(ensureEffectContext());
      await Promise.all(contexts.map(context => context.resume()));
      const ready = contexts.every(context => context.state === 'running');
      if (ready && (pausedRef.current || document.hidden)) await Promise.all(contexts.map(context => context.suspend()));
      setAudioReady(ready);
      if (ready) setAudioError('');
      else if (showError) setAudioError('Your browser paused sound. Tap Try sound again.');
      return ready;
    } catch (error) {
      if (showError) setAudioError(error instanceof Error ? error.message : 'This browser could not start sound.');
      return false;
    }
  }

  function cancelEffects() {
    effectGeneration.current += 1;
    for (const timer of timers.current) clearTimeout(timer);
    timers.current.clear();
    for (const source of activeEffects.current) {
      try { source.stop(); } catch { /* It may already have ended. */ }
    }
    activeEffects.current.clear();
  }

  function stopMusic() {
    musicGeneration.current += 1;
    const source = musicSource.current;
    musicSource.current = null;
    playingTrack.current = null;
    if (source) {
      source.onended = null;
      try { source.stop(); } catch { /* It may already have ended. */ }
      source.disconnect();
    }
  }

  async function loadMusic(nextTrack: MusicTrack): Promise<AudioBuffer> {
    const cached = musicBuffers.current.get(nextTrack);
    if (cached) return cached;
    const inFlight = musicLoads.current.get(nextTrack);
    if (inFlight) return inFlight;
    const context = musicContext.current;
    if (!context) throw new Error('Music needs to be enabled first.');
    const load = fetch(MUSIC[nextTrack])
      .then(response => {
        if (!response.ok) throw new Error(`Could not load music (${response.status}).`);
        return response.arrayBuffer();
      })
      .then(data => context.decodeAudioData(data))
      .then(buffer => { musicBuffers.current.set(nextTrack, buffer); return buffer; })
      .finally(() => musicLoads.current.delete(nextTrack));
    musicLoads.current.set(nextTrack, load);
    return load;
  }

  async function startMusic(nextTrack: MusicTrack) {
    const context = musicContext.current;
    const gain = musicGain.current;
    if (!context || !gain) return;
    const generation = ++musicGeneration.current;
    const buffer = await loadMusic(nextTrack);
    if (generation !== musicGeneration.current || !musicEnabledRef.current || pausedRef.current || document.hidden) return;
    const previous = musicSource.current;
    if (previous) {
      previous.onended = null;
      try { previous.stop(); } catch { /* It may already have ended. */ }
      previous.disconnect();
    }
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    source.connect(gain);
    musicSource.current = source;
    playingTrack.current = nextTrack;
    source.start();
  }

  async function loadEffect(sound: EffectSound): Promise<AudioBuffer> {
    const cached = effectBuffers.current.get(sound);
    if (cached) return cached;
    const inFlight = effectLoads.current.get(sound);
    if (inFlight) return inFlight;
    const context = effectContext.current;
    if (!context) throw new Error('Effects need to be enabled first.');
    const load = fetch(EFFECTS[sound])
      .then(response => {
        if (!response.ok) throw new Error(`Could not load ${sound} (${response.status}).`);
        return response.arrayBuffer();
      })
      .then(data => context.decodeAudioData(data))
      .then(buffer => { effectBuffers.current.set(sound, buffer); return buffer; })
      .finally(() => effectLoads.current.delete(sound));
    effectLoads.current.set(sound, load);
    return load;
  }

  function playEffect(cue: EffectCue | { sound: 'transition'; delayMs: number }) {
    const generation = effectGeneration.current;
    const play = async () => {
      if (generation !== effectGeneration.current || !effectsRef.current || pausedRef.current || document.hidden) return;
      try {
        const context = effectContext.current;
        if (!context || context.state !== 'running') return;
        const buffer = await loadEffect(cue.sound);
        if (generation !== effectGeneration.current || !effectsRef.current || pausedRef.current || document.hidden) return;
        const source = context.createBufferSource();
        const gain = context.createGain();
        gain.gain.value = cue.sound === 'crystal' ? 0.24 : cue.sound === 'transition' ? 0.2 : 0.16;
        source.buffer = buffer;
        source.connect(gain).connect(context.destination);
        source.onended = () => activeEffects.current.delete(source);
        activeEffects.current.add(source);
        source.start();
      } catch (error) {
        if (generation === effectGeneration.current) setAudioError(error instanceof Error ? error.message : 'Game effects could not play.');
      }
    };
    if (cue.delayMs === 0) void play();
    else {
      const timer = window.setTimeout(() => { timers.current.delete(timer); void play(); }, cue.delayMs);
      timers.current.add(timer);
    }
  }

  useEffect(() => {
    return () => {
      stopMusic();
      void musicContext.current?.close();
      musicContext.current = null;
      musicGain.current = null;
      cancelEffects();
      void effectContext.current?.close();
      effectContext.current = null;
    };
  }, []);

  useEffect(() => {
    // Try quietly first for browsers which already granted playback. If policy blocks it, the first
    // ordinary interaction retries inside the trusted event without changing the saved preference.
    void unlockAudio(false);
    const unlockFromInteraction = () => { void unlockAudio(true); };
    window.addEventListener('pointerdown', unlockFromInteraction, { capture: true });
    window.addEventListener('keydown', unlockFromInteraction, { capture: true });
    return () => {
      window.removeEventListener('pointerdown', unlockFromInteraction, { capture: true });
      window.removeEventListener('keydown', unlockFromInteraction, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (musicGain.current) musicGain.current.gain.value = narration.playing ? 0.055 : 0.16;
    const context = musicContext.current;
    if (!musicEnabled || !track) {
      stopMusic();
      return;
    }
    if (paused || document.hidden) {
      void context?.suspend();
      return;
    }
    if (musicSource.current && playingTrack.current === track) {
      void context?.resume();
      return;
    }
    stopMusic();
    void context?.resume().then(() => {
      if (context.state === 'running') return startMusic(track);
    }).catch(() => { /* A later trusted interaction retries playback. */ });
  }, [track, musicEnabled, paused, narration.playing, audioReady]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        void musicContext.current?.suspend();
        cancelEffects();
      } else if (musicEnabled && track && !pausedRef.current) {
        void musicContext.current?.resume().then(() => {
          if (!musicSource.current || playingTrack.current !== track) {
            stopMusic();
            return startMusic(track);
          }
        }).catch(() => { setAudioReady(false); });
      }
      if (!document.hidden) {
        const context = effectContext.current;
        if (effectsRef.current && context?.state === 'suspended') void context.resume().catch(() => { setAudioReady(false); });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [musicEnabled, track]);

  useEffect(() => {
    if (paused) {
      cancelEffects();
    } else if (!document.hidden && effectsRef.current && effectContext.current?.state === 'suspended') {
      void effectContext.current.resume().catch(() => { setAudioReady(false); });
    }
  }, [paused]);

  useEffect(() => client.onEvent((event) => {
    if (!effectsRef.current || pausedRef.current || document.hidden || event.instanceId !== instanceRef.current) return;
    for (const cue of effectCuesForEvent(event)) playEffect(cue);
  }), [client]);

  const previousChild = useRef(view.child?.instanceId ?? null);
  useEffect(() => {
    const next = view.child?.instanceId ?? null;
    const enteredNewGame = shouldPlayActivityTransition(previousChild.current, next);
    previousChild.current = next;
    if (enteredNewGame && effectsEnabled && !paused && !document.hidden) {
      playEffect({ sound: 'transition', delayMs: 0 });
    }
  }, [view.child?.instanceId, effectsEnabled, paused]);

  return <>
    {children}
    <aside className="game-audio" aria-label="Sound settings">
      <details>
        <summary>
          <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 9v6h4l5 4V5L8 9H4Zm12.5-.5a5 5 0 0 1 0 7m2.5-9.5a8.5 8.5 0 0 1 0 12" /></svg>
          Sound
        </summary>
        <div className="game-audio__menu">
          <p>Only changes sound on this device</p>
          <label><span>Music</span><input type="checkbox" checked={musicEnabled} onChange={event => {
            const next = event.currentTarget.checked;
            musicEnabledRef.current = next;
            setMusicEnabled(next);
            savePreference(MUSIC_PREFERENCE, next);
            setAudioError('');
            if (!next) { stopMusic(); return; }
            void unlockAudio(true);
          }} /></label>
          <label><span>Game effects</span><input type="checkbox" checked={effectsEnabled} disabled={effectsLoading} onChange={event => {
            const next = event.currentTarget.checked;
            effectsRef.current = next;
            setEffectsEnabled(next);
            savePreference(EFFECTS_PREFERENCE, next);
            setAudioError('');
            if (!next) { cancelEffects(); return; }
            setEffectsLoading(true);
            void unlockAudio(true).finally(() => setEffectsLoading(false));
          }} /></label>
          {narration.available ? <>
            <label><span>Host voice</span><input type="checkbox" checked={narration.enabled} disabled={!narration.canToggle} onChange={narration.toggle} /></label>
            {narration.enabled ? <button type="button" disabled={!narration.canReplay} onClick={narration.replayHost}>Replay host</button> : null}
          </> : null}
          {audioError || narration.error ? <div className="game-audio__error" role="status">
            <span>{audioError || narration.error}</span>
            {audioError ? <button type="button" onClick={() => void unlockAudio(true)}>Try sound again</button> : null}
          </div> : null}
        </div>
      </details>
    </aside>
  </>;
}
